import React, { useRef, useState, useMemo } from "react";
import { Button, Box, Typography } from "@mui/material";
import { Download, Image } from "@mui/icons-material";
import html2canvas from "html2canvas";
// @ts-ignore
import html2pdf from "html2pdf.js";
import jsPDF from "jspdf";
import { useTranslation } from "react-i18next";
import { useSubjectMarksMappingByClassName, useVocationalSubjects } from "../../hooks/useSubjects";
import SchoolRegisterCoverPage from "./SchoolRegisterCoverPage";

// Container Styles
const containerStyle = {
  background: "white",
  minHeight: "100vh",
  padding: "20px",
  margin: "0",
  fontFamily: "Arial, sans-serif",
};

const mainBoxStyle = {
  background: "#fff",
  margin: "10px auto",
  width: "297mm", // A4 landscape width
  maxWidth: "297mm",
  minHeight: "210mm", // A4 landscape height
  position: "relative" as const,
  padding: "15px",
  boxSizing: "border-box" as const,
  border: "3px solid #000",
  outline: "1px solid #000",
};

// Header Styles
const headerStyle = {
  textAlign: "center" as const,
  marginBottom: "15px",
  borderBottom: "2px solid #000",
  paddingBottom: "10px",
};

const titleStyle = {
  fontSize: "22px",
  fontWeight: "bold",
  color: "#b22222",
  margin: "0 0 8px 0",
  textTransform: "uppercase" as const,
};

const subtitleStyle = {
  fontSize: "16px",
  fontWeight: "bold",
  color: "#000",
  margin: "5px 0",
};

const formulaStyle = {
  fontSize: "12px",
  color: "#000",
  margin: "5px 0",
  fontStyle: "italic" as const,
};

const pageNumberStyle = {
  position: "absolute" as const,
  top: "15px",
  right: "15px",
  fontSize: "14px",
  fontWeight: "bold",
  backgroundColor: "#ffeb3b",
  padding: "4px 8px",
  border: "1px solid #000",
};

// Student Section Styles
const getStudentSectionStyle = (isLast: boolean) => ({
  display: "flex",
  marginBottom: isLast ? "0px" : "5px",
  border: "2px solid #000",
  minHeight: "250px",
});

const leftSectionStyle = {
  width: "25%",
  borderRight: "2px solid #000",
  padding: "10px",
  backgroundColor: "#fff",
};

const rightSectionStyle = {
  width: "75%",
  padding: "10px",
  backgroundColor: "#fff",
};

const studentInfoRowStyle: React.CSSProperties = {
  display: "flex",
  marginBottom: "0.1px",
  fontSize: "12px",
  alignItems: "flex-start",
};

const studentLabelStyle: React.CSSProperties = {
  fontWeight: "bold",
  minWidth: "140px",
  marginRight: "5px",
};

const studentValueStyle: React.CSSProperties = {
  flex: 1,
  borderBottom: "1px solid #000",
  minHeight: "18px",
  paddingLeft: "5px",
  minWidth: 0,
  whiteSpace: "normal",
  overflowWrap: "break-word",
  wordBreak: "break-word",
};

const serialNumberStyle = {
  display: "flex",
  alignItems: "center",
  marginBottom: "10px",
  fontSize: "13px",
  fontWeight: "bold",
};

const serialNumberBoxStyle = {
  backgroundColor: "#ffeb3b",
  padding: "4px 8px",
  border: "1px solid #000",
  margin: "0 8px",
  minWidth: "30px",
  textAlign: "center" as const,
};

// Table Styles
const marksTableStyle = {
  width: "100%",
  borderCollapse: "collapse" as const,
  fontSize: "9px",
  border: "1px solid #000",
};

const marksThStyle = {
  border: "1px solid #000",
  background: "#e9ecef",
  padding: "6px 3px",
  textAlign: "center" as const,
  fontWeight: "bold",
  fontSize: "8px",
};

const marksTotalStyle = {
  border: "1px solid #000",
  background: "#e9ecef",
  padding: "6px 3px",
  textAlign: "center" as const,
  fontWeight: "bold",
  fontSize: "8px",
};

const marksTdStyle = {
  border: "1px solid #000",
  padding: "4px 2px",
  textAlign: "center" as const,
  fontSize: "8px",
};

const subjectTdStyle = {
  border: "1px solid #000",
  padding: "4px 2px",
  textAlign: "left" as const,
  fontSize: "8px",
  fontWeight: "bold",
};

const totalRowStyle = {
  background: "#f8f9fa",
  fontWeight: "bold",
};

// Download Button Styles
const downloadButtonStyle = {
  background: "#5fa8e6",
  color: "#fff",
  border: "2px solid #fff",
  borderRadius: "8px",
  padding: "12px 24px",
  fontSize: "14px",
  fontWeight: "bold",
  margin: "20px 10px",
  cursor: "pointer",
  textTransform: "uppercase" as const,
  letterSpacing: "0.5px",
};

interface Subject {
  subjectCode?: string;
  subjectName: string;
  maxTheory?: number;
  maxPractical?: number;
  minTheory?: number;
  minPractical?: number;
  quarterlyTheory?: number;
  quarterlyPractical?: number;
  halfYearlyTheory?: number;
  halfYearlyPractical?: number;
  annualTheory?: number;
  annualPractical?: number;
  originalPracticalMarks?: number;
  quarterlyWeightageTH?: number;
  quarterlyWeightagePR?: number;
  halfYearlyWeightageTH?: number;
  halfYearlyWeightagePR?: number;
  annualWeightageTH?: number;
  annualWeightagePR?: number;
  totalWeightageTH?: number;
  totalWeightagePR?: number;
  totalMarksTh?: number;
  totalMarksPr?: number;
  remark?: string;
}

interface Student {
  serialNumber: number;
  rollNumber?: string;
  enrolmentNo?: string;
  scholarNo?: string;
  medium?: string;
  studentName?: string;
  fatherName?: string;
  motherName?: string;
  dateOfBirth?: string;
  result?: string;
  percentGrade?: string;
  subjects?: Subject[];
}

interface SchoolRegisterTemplateProps {
  students: Student[];
  school?: any;
  district?: string;
  block?: string;
  diseCode?: string;
  mpbseCode?: string;
  academicYear?: string;
  className?: string;
  classId?: number;
}

const SchoolRegisterTemplate9thClass: React.FC<SchoolRegisterTemplateProps> = ({
  students,
  school,
  district = "",
  block = "",
  diseCode = "",
  mpbseCode = "",
  academicYear = "2024-25",
  className = "9th",
  classId,
}) => {
  const getBaseClassName = (name: string): string => {
    if (!name) return "";
    const match = name.match(/^(\d+(?:th|st|nd|rd)?)/i);
    return match ? match[1] : name.split(/\s+/)[0];
  };

  const { t } = useTranslation();
  const [isDownloadingImage, setIsDownloadingImage] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const templateRef = useRef<HTMLDivElement>(null);

  const renderMark = (value: any) => {
    if (value === -1) {
      return <span style={{ color: "red", fontWeight: "bold" }}>ABS</span>;
    }
    return value ?? "";
  };

  // Fetch vocational subjects for 40/60 detection
  const { data: vocationalSubjects } = useVocationalSubjects(classId);

  // Fetch subject marks mapping for dynamic max/min marks
  const baseClassName = getBaseClassName(className);
  const { data: subjectMarksMapping } = useSubjectMarksMappingByClassName(baseClassName);

  const subjectMarksMap = useMemo(() => {
    const map = new Map<string, any>();
    if (subjectMarksMapping) {
      subjectMarksMapping.forEach((m: any) => {
        const code = String(m.subjectCode || "").trim();
        if (code) map.set(code, m);
      });
    }
    return map;
  }, [subjectMarksMapping]);

  const getStudentTotals = (subjects: Subject[]) => {
    let totalObtainedTh = 0;
    let totalObtainedPr = 0;
    let totalMaxTh = 0;
    let totalMaxPr = 0;
    let hasFailed = false;

    subjects.forEach((subject) => {
      const subjectCode = String(subject.subjectCode || "").trim();
      const subjectName = String(subject.subjectName || "").trim().toLowerCase();
      const mapping = subjectMarksMap.get(subjectCode);

      const isVocational = vocationalSubjects?.has(subjectName) ||
        (mapping?.maxTheoryMarks === 40 && mapping?.maxPracticalMarks === 60) ||
        (mapping?.theoryMarks === 40 && mapping?.practicalMarks === 60);

      const maxTh = mapping?.theoryMarks ?? mapping?.maxTheoryMarks ?? (isVocational ? 40 : (subject.maxTheory ?? 75));
      const maxPr = mapping?.practicalMarks ?? mapping?.maxPracticalMarks ?? (isVocational ? 60 : (subject.maxPractical ?? 25));
      const minTh = mapping?.thPassMarks ?? mapping?.minTheoryMarks ?? (isVocational ? 13 : (subject.minTheory ?? 25));
      const minPr = mapping?.prPassMarks ?? mapping?.minPracticalMarks ?? (isVocational ? 20 : (subject.minPractical ?? 8));

      const skipsWeightage = maxTh === 40 && maxPr === 60;

      const qWt = skipsWeightage ? 0 : Number((((subject.quarterlyTheory || 0) + (subject.quarterlyPractical || 0)) * 0.05).toFixed(2));
      const hWt = skipsWeightage ? 0 : Number((((subject.halfYearlyTheory || 0) + (subject.halfYearlyPractical || 0)) * 0.05).toFixed(2));

      const totalTh = subject.annualTheory || 0;
      const practicalBase = isVocational ? (subject.annualPractical ?? 0) : (subject.originalPracticalMarks ?? 0);
      const totalPr = Math.round(practicalBase + qWt + hWt);

      totalObtainedTh += (totalTh > 0 ? totalTh : 0);
      totalObtainedPr += (totalPr > 0 ? totalPr : 0);
      totalMaxTh += maxTh;
      totalMaxPr += maxPr;

      // Result criteria: fail if any subject marks are below minimum (considering -1 as ABSent/0)
      const isAbsent = totalTh === -1 || (isVocational ? subject.annualPractical === -1 : subject.originalPracticalMarks === -1);
      if (isAbsent || totalTh < minTh || totalPr < minPr) {
        hasFailed = true;
      }
    });

    const totalObtained = totalObtainedTh + totalObtainedPr;
    const totalMax = totalMaxTh + totalMaxPr;
    const percentage = totalMax > 0 ? ((totalObtained / totalMax) * 100).toFixed(2) + "%" : "";
    const result = hasFailed ? "FAIL" : "PASS";

    return { totalObtainedTh, totalObtainedPr, totalMaxTh, totalMaxPr, percentage, result };
  };

  // Chunk students into groups of 3
  const studentChunks: Student[][] = [];
  const studentsToProcess = students;

  if (studentsToProcess.length === 0) {
    // Show one empty page if no students
    const emptyChunk: Student[] = [];
    for (let j = 0; j < 3; j++) {
      emptyChunk.push({
        serialNumber: j + 1,
        subjects: [],
      });
    }
    studentChunks.push(emptyChunk);
  } else {
    for (let i = 0; i < studentsToProcess.length; i += 3) {
      const chunk = [...studentsToProcess.slice(i, i + 3)];
      // Pad the last chunk to 3 students
      while (chunk.length < 3) {
        chunk.push({
          serialNumber: i + chunk.length + 1,
          subjects: [],
        });
      }
      studentChunks.push(chunk);
    }
  }

  // Function to download template as image
  const downloadAsImage = async () => {
    if (!templateRef.current) return;

    setIsDownloadingImage(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 300));

      const canvas = await html2canvas(templateRef.current, {
        useCORS: true,
        allowTaint: false,
        background: "#ffffff",
        scale: 2, // Higher scale for better quality
        logging: false,
      } as any);

      const link = document.createElement("a");
      link.download = `school-register-${academicYear}-${new Date().toISOString().split("T")[0]}.png`;
      link.href = canvas.toDataURL("image/png", 1.0);
      link.click();
    } catch (error) {
      console.error("Image download error:", error);
      alert(t("failed_to_download_image") || "Failed to download image");
    } finally {
      setIsDownloadingImage(false);
    }
  };

  // Function to download template as PDF
  const downloadAsPdf = async () => {
    if (!templateRef.current) {
      alert(t("template_not_ready") || "Template not ready");
      return;
    }

    setIsDownloadingPdf(true);
    try {
      // Find cover + all page elements (cover must be first in DOM)
      const pageElements = templateRef.current.querySelectorAll(
        ".school-register-cover-page, .school-register-page"
      );
      if (pageElements.length === 0) {
        throw new Error("No pages found to export");
      }

      const pdf = new jsPDF({
        unit: "mm",
        format: "a4",
        orientation: "landscape",
        compress: true,
      });

      for (let i = 0; i < pageElements.length; i++) {
        const pageElement = pageElements[i] as HTMLElement;

        // Brief delay between captures to ensure rendering
        await new Promise((resolve) => setTimeout(resolve, 300));

        const canvas = await html2canvas(pageElement, {
          useCORS: true,
          allowTaint: false,
          background: "#ffffff",
          scale: 2, // Higher scale for better quality
          logging: false,
        } as any);

        const imgData = canvas.toDataURL("image/jpeg", 0.95);
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        // Add new page if not the first one
        if (i > 0) {
          pdf.addPage();
        }

        // Add image to the current PDF page
        pdf.addImage(imgData, "JPEG", 0, 0, pageWidth, pageHeight);
      }

      const fileName = `school-register-${academicYear}-${new Date().toISOString().split("T")[0]}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error("PDF download error:", error);
      alert(t("failed_to_download_pdf") || "Failed to download PDF");
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  // Render marks table for a student
  const renderMarksTable = (student: Student) => {
    const subjects = student.subjects || [];
    const numRows = Math.max(subjects.length, 6); // Minimum 6 rows for empty template

    return (
      <table style={marksTableStyle}>
        <tbody>
          {/* Header Row 1 */}
          <tr>
            <th style={marksThStyle} rowSpan={3}>
              विषय / SUBJECT
            </th>
            <th style={marksThStyle} colSpan={2} rowSpan={2}>
              अधिकतम अंक / MAX. MARKS
            </th>
            <th style={marksThStyle} colSpan={2} rowSpan={2}>
              न्यूनतम अंक / MIN. MARKS
            </th>
            <th style={marksThStyle} colSpan={2}>
              त्रैमासिक परीक्षा / QUARTERLY EXAM
            </th>
            <th style={marksThStyle} colSpan={2}>
              अर्धवार्षिक परीक्षा / HALF YEARLY EXAM
            </th>
            <th style={marksThStyle} colSpan={2}>
              वार्षिक परीक्षा / ANNUAL EXAM
            </th>
            <th style={marksThStyle} colSpan={4}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  lineHeight: 1.2,
                  padding: "4px 2px",
                }}
              >
                <span style={{ fontWeight: "bold", fontSize: "8px" }}>
                  प्रायोगिक/प्रोजेक्ट
                </span>
              </div>
            </th>
            <th
              style={{
                ...marksThStyle,
                verticalAlign: "middle",
                textAlign: "center",
                writingMode: "vertical-rl" as const,
                textOrientation: "mixed" as const,
              }}
              rowSpan={3}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <span style={{ fontWeight: "bold", fontSize: "8px" }}>
                  विशेष / REMARK
                </span>
              </div>
            </th>
          </tr>
          {/* Header Row 2 */}
          <tr>
            <th style={marksThStyle} colSpan={2}>
              प्राप्तांक / OBTAINED MARKS
            </th>
            <th style={marksThStyle} colSpan={2}>
              प्राप्तांक / OBTAINED MARKS
            </th>
            <th style={marksThStyle} colSpan={2}>
              प्राप्तांक / OBTAINED MARKS
            </th>
            <th style={marksThStyle} colSpan={1}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  lineHeight: 1.2,
                  padding: "3px 2px",
                }}
              >
                <span style={{ fontWeight: "bold", fontSize: "7px" }}>
                  त्रैमासिक परीक्षा का 5%
                </span>
                <span style={{ fontWeight: "bold", fontSize: "7px" }}>
                  QUARTERLY EXAM 5%
                </span>
              </div>
            </th>
            <th style={marksThStyle} colSpan={1}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  lineHeight: 1.2,
                  padding: "3px 2px",
                }}
              >
                <span style={{ fontWeight: "bold", fontSize: "7px" }}>
                  अर्धवार्षिक परीक्षा का 5%
                </span>
                <span style={{ fontWeight: "bold", fontSize: "7px" }}>
                  HALF YEARLY EXAM 5%
                </span>
              </div>
            </th>
            <th style={marksThStyle} colSpan={2} >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  lineHeight: 1.2,
                  padding: "3px 2px",
                }}
              >
                <span style={{ fontWeight: "bold", fontSize: "7px" }}>
                  Total
                </span>
              </div>
            </th>
          </tr>
          {/* Header Row 3 */}
          <tr>
            <th style={marksThStyle}>TH</th>
            <th style={marksThStyle}>PR</th>
            <th style={marksThStyle}>TH</th>
            <th style={marksThStyle}>PR</th>
            <th style={marksThStyle}>TH</th>
            <th style={marksThStyle}>PR</th>
            <th style={marksThStyle}>TH</th>
            <th style={marksThStyle}>PR</th>
            <th style={marksThStyle}>TH</th>
            <th style={marksThStyle}>PR</th>
            <th style={marksThStyle}>PR</th>
            <th style={marksThStyle}>PR</th>
            <th style={marksThStyle}>TH</th>
            <th style={marksThStyle}>PR</th>
          </tr>
          {/* Subject Rows */}
          {Array.from({ length: numRows }).map((_, idx) => {
            const subject = subjects[idx];
            if (subject) {
              const subjectCode = String(subject.subjectCode || "").trim();
              const subjectName = String(subject.subjectName || "").trim().toLowerCase();
              const mapping = subjectMarksMap.get(subjectCode);

              // Vocational subjects detection (prioritize name check like MarksheetTemplate)
              const isVocational = vocationalSubjects?.has(subjectName) ||
                (mapping?.maxTheoryMarks === 40 && mapping?.maxPracticalMarks === 60) ||
                (mapping?.theoryMarks === 40 && mapping?.practicalMarks === 60);

              const maxTh = mapping?.theoryMarks ?? mapping?.maxTheoryMarks ?? (isVocational ? 40 : (subject.maxTheory ?? 75));
              const maxPr = mapping?.practicalMarks ?? mapping?.maxPracticalMarks ?? (isVocational ? 60 : (subject.maxPractical ?? 25));
              const minTh = mapping?.thPassMarks ?? mapping?.minTheoryMarks ?? (isVocational ? 13 : (subject.minTheory ?? 25));
              const minPr = mapping?.prPassMarks ?? mapping?.minPracticalMarks ?? (isVocational ? 20 : (subject.minPractical ?? 8));

              // Standard vocational 40/60 distribution check for weightage skip
              const skipsWeightage = maxTh === 40 && maxPr === 60;

              const qWt = skipsWeightage ? 0 : Number((((subject.quarterlyTheory || 0) + (subject.quarterlyPractical || 0)) * 0.05).toFixed(2));
              const hWt = skipsWeightage ? 0 : Number((((subject.halfYearlyTheory || 0) + (subject.halfYearlyPractical || 0)) * 0.05).toFixed(2));

              const totalTh = subject.annualTheory || 0;
              const practicalBase = isVocational ? (subject.annualPractical ?? 0) : (subject.originalPracticalMarks ?? 0);
              const totalPr = Math.round(practicalBase + qWt + hWt);

              return (
                <tr key={idx}>
                  <td style={subjectTdStyle}>
                    {subject.subjectCode?.toUpperCase()}{" "}
                    {subject.subjectName?.toUpperCase()}
                  </td>
                  <td style={marksTdStyle}>{maxTh}</td>
                  <td style={marksTdStyle}>{maxPr}</td>
                  <td style={marksTdStyle}>{minTh}</td>
                  <td style={marksTdStyle}>{minPr}</td>
                  <td style={marksTdStyle}>{renderMark(subject.quarterlyTheory)}</td>
                  <td style={marksTdStyle}>{renderMark(subject.quarterlyPractical)}</td>
                  <td style={marksTdStyle}>{renderMark(subject.halfYearlyTheory)}</td>
                  <td style={marksTdStyle}>{renderMark(subject.halfYearlyPractical)}</td>
                  <td style={marksTdStyle}>{renderMark(subject.annualTheory)}</td>
                  <td style={marksTdStyle}>
                    {isVocational ? renderMark(subject.annualPractical) : renderMark(subject.originalPracticalMarks)}
                  </td>
                  <td style={marksTdStyle}>{skipsWeightage ? "" : (qWt || "0")}</td>
                  <td style={marksTdStyle}>{skipsWeightage ? "" : (hWt || "0")}</td>
                  <td style={marksTdStyle}>{totalTh || ""}</td>
                  <td style={marksTdStyle}>{totalPr || ""}</td>
                  <td style={marksTdStyle}>{subject.remark || ""}</td>
                </tr>
              );
            }
            return (
              <tr key={idx}>
                {Array.from({ length: 16 }).map((_, emptyIdx) => (
                  <td key={emptyIdx} style={{ border: "1px solid #000", padding: "10px 5px" }}></td>
                ))}
              </tr>
            );
          })}
          {/* Total Row */}
          <tr style={totalRowStyle}>
            <td style={subjectTdStyle}>TOTAL</td>
            <td style={marksTdStyle}>
              {getStudentTotals(subjects).totalMaxTh || ""}
            </td>
            <td style={marksTdStyle}>
              {getStudentTotals(subjects).totalMaxPr || ""}
            </td>
            <td style={marksTdStyle}></td>
            <td style={marksTdStyle}></td>
            <td style={marksTdStyle}>{subjects.reduce((sum, s) => sum + (s.quarterlyTheory && s.quarterlyTheory > 0 ? s.quarterlyTheory : 0), 0) || ""}</td>
            <td style={marksTdStyle}>{subjects.reduce((sum, s) => sum + (s.quarterlyPractical && s.quarterlyPractical > 0 ? s.quarterlyPractical : 0), 0) || ""}</td>
            <td style={marksTdStyle}>{subjects.reduce((sum, s) => sum + (s.halfYearlyTheory && s.halfYearlyTheory > 0 ? s.halfYearlyTheory : 0), 0) || ""}</td>
            <td style={marksTdStyle}>{subjects.reduce((sum, s) => sum + (s.halfYearlyPractical && s.halfYearlyPractical > 0 ? s.halfYearlyPractical : 0), 0) || ""}</td>
            <td style={marksTdStyle}>{getStudentTotals(subjects).totalObtainedTh || ""}</td>
            <td style={marksTdStyle}>
              {subjects.reduce((sum, s) => {
                const code = String(s.subjectCode || "").trim();
                const name = String(s.subjectName || "").trim().toLowerCase();
                const mapping = subjectMarksMap.get(code);
                const isVoc = vocationalSubjects?.has(name) || (mapping?.maxTheoryMarks === 40 && mapping?.maxPracticalMarks === 60) || (mapping?.theoryMarks === 40 && mapping?.practicalMarks === 60);
                const val = isVoc ? (s.annualPractical ?? 0) : (s.originalPracticalMarks ?? 0);
                return sum + (val > 0 ? val : 0);
              }, 0) || ""}
            </td>
            <td style={marksTdStyle}>
              {subjects.reduce((sum, s) => {
                const code = String(s.subjectCode || "").trim();
                const name = String(s.subjectName || "").trim().toLowerCase();
                const mapping = subjectMarksMap.get(code);

                const isVocational = vocationalSubjects?.has(name) ||
                  (mapping?.maxTheoryMarks === 40 && mapping?.maxPracticalMarks === 60) ||
                  (mapping?.theoryMarks === 40 && mapping?.practicalMarks === 60);

                if (isVocational) return sum;
                const theory = s.quarterlyTheory && s.quarterlyTheory > 0 ? s.quarterlyTheory : 0;
                const practical = s.quarterlyPractical && s.quarterlyPractical > 0 ? s.quarterlyPractical : 0;
                return sum + Number(((theory + practical) * 0.05).toFixed(2));
              }, 0).toFixed(2) || ""}
            </td>
            <td style={marksTdStyle}>
              {subjects.reduce((sum, s) => {
                const code = String(s.subjectCode || "").trim();
                const name = String(s.subjectName || "").trim().toLowerCase();
                const mapping = subjectMarksMap.get(code);

                const isVocational = vocationalSubjects?.has(name) ||
                  (mapping?.maxTheoryMarks === 40 && mapping?.maxPracticalMarks === 60) ||
                  (mapping?.theoryMarks === 40 && mapping?.practicalMarks === 60);

                if (isVocational) return sum;
                const theory = s.halfYearlyTheory && s.halfYearlyTheory > 0 ? s.halfYearlyTheory : 0;
                const practical = s.halfYearlyPractical && s.halfYearlyPractical > 0 ? s.halfYearlyPractical : 0;
                return sum + Number(((theory + practical) * 0.05).toFixed(2));
              }, 0).toFixed(2) || ""}
            </td>
            <td style={marksTdStyle}>{getStudentTotals(subjects).totalObtainedTh || ""}</td>
            <td style={marksTdStyle}>{getStudentTotals(subjects).totalObtainedPr || ""}</td>
            <td style={marksTdStyle}></td>
          </tr>
          {/* Additional Subject Row */}
          <tr>
            <td
              colSpan={16}
              style={{
                ...subjectTdStyle,
                border: "1px solid #000",
                fontWeight: "bold",
                fontSize: "9px",
              }}
            >
              Additional Subject
            </td>
          </tr>
        </tbody>
      </table>
    );
  };

  return (
    <div style={containerStyle}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @media print {
            .no-print { display: none !important; }
            .school-register-cover-page {
              margin: 0 !important;
              page-break-after: always !important;
              box-shadow: none !important;
              border: 3px solid #000 !important;
            }
            .school-register-page { 
              margin: 0 !important; 
              page-break-after: always !important;
              box-shadow: none !important;
              border: 3px solid #000 !important;
            }
          }
        `}
      </style>
      <div ref={templateRef}>
        <SchoolRegisterCoverPage school={school} academicYear={academicYear} className={className} />
        {studentChunks.map((chunk, pageIdx) => (
          <div
            key={pageIdx}
            className="school-register-page"
            style={{
              ...mainBoxStyle,
              marginBottom: pageIdx === studentChunks.length - 1 ? "0" : "40px",
              pageBreakAfter: "always",
            }}
          >
            {/* Page Number */}
            <div style={pageNumberStyle}>
              PAGE NO. {pageIdx + 1}
            </div>

            {/* Header */}
            <div style={headerStyle}>
              <div style={titleStyle}>Annual Examination {academicYear}</div>
              <div style={subtitleStyle}>
                अधिभार आधारित वार्षिक परीक्षाफल / WEIGHTAGE BASED ANNUAL EXAMINATION
                RESULT
              </div>
              <div style={formulaStyle}>
                परीक्षा अधिभार (त्रैमासिक 5% + अर्धवार्षिक 5% + वार्षिक परियोजना 15% = 25%) /
                (QUARTERLY 5% + HALF YEARLY 5% + ANNUAL PROJECT 15% = 100%)
              </div>
            </div>

            {/* School/District Info */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "10px",
                fontSize: "11px",
                fontWeight: "bold",
              }}
            >
              <div>DISE CODE- {diseCode}</div>
              <div>DISTRICT - {district}</div>
              <div>BLOCK - {block}</div>
              <div>MPBSE CODE- {mpbseCode}</div>
            </div>

            {/* Student Sections */}
            {chunk.map((student, sectionIdx) => (
              <div key={sectionIdx} style={getStudentSectionStyle(sectionIdx === chunk.length - 1)}>
                {/* Left Section - Student Info */}
                <div style={leftSectionStyle}>
                  <div style={serialNumberStyle}>
                    S.N.-
                    <div style={serialNumberBoxStyle}>{student.serialNumber}</div>
                    CLASS - {className}
                  </div>
                  <div style={studentInfoRowStyle}>
                    <div style={studentLabelStyle}>ROLL NO.-</div>
                    <div style={studentValueStyle}>{student.rollNumber || ""}</div>
                  </div>
                  <div style={studentInfoRowStyle}>
                    <div style={studentLabelStyle}>ENROLMENT NO.-</div>
                    <div style={studentValueStyle}>
                      {student.enrolmentNo || ""}
                    </div>
                  </div>
                  <div style={studentInfoRowStyle}>
                    <div style={studentLabelStyle}>SCHOLAR NO.-</div>
                    <div style={studentValueStyle}>{student.scholarNo || ""}</div>
                  </div>
                  <div style={studentInfoRowStyle}>
                    <div style={studentLabelStyle}>MEDIUM/SSSMID-</div>
                    <div style={studentValueStyle}>{student.medium || ""}</div>
                  </div>
                  <div style={studentInfoRowStyle}>
                    <div style={studentLabelStyle}>STUDENT'S NAME -</div>
                    <div style={studentValueStyle}>
                      {student.studentName || ""}
                    </div>
                  </div>
                  <div style={studentInfoRowStyle}>
                    <div style={studentLabelStyle}>FATHER'S NAME SHRI-</div>
                    <div style={studentValueStyle}>{student.fatherName || ""}</div>
                  </div>
                  <div style={studentInfoRowStyle}>
                    <div style={studentLabelStyle}>MOTHER'S NAME SMT.-</div>
                    <div style={studentValueStyle}>{student.motherName || ""}</div>
                  </div>
                  <div style={studentInfoRowStyle}>
                    <div style={studentLabelStyle}>DATE OF BIRTH-</div>
                    <div style={studentValueStyle}>
                      {student.dateOfBirth || ""}
                    </div>
                  </div>
                  <div style={studentInfoRowStyle}>
                    <div style={studentLabelStyle}>RESULT</div>
                    <div style={studentValueStyle}>
                      {student.result || getStudentTotals(student.subjects || []).result}
                    </div>
                  </div>
                  <div style={studentInfoRowStyle}>
                    <div style={studentLabelStyle}>PERCENT/GRADE</div>
                    <div style={studentValueStyle}>
                      {student.percentGrade || getStudentTotals(student.subjects || []).percentage}
                    </div>
                  </div>
                </div>

                {/* Right Section - Marks Table */}
                <div style={rightSectionStyle}>{renderMarksTable(student)}</div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Download Buttons */}
      <Box className="no-print" sx={{ marginTop: "16px", textAlign: "center" }}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          {/* Image Download Button */}
          <Button
            variant="contained"
            startIcon={<Image />}
            disabled={isDownloadingImage}
            onClick={downloadAsImage}
            sx={{
              padding: "12px 24px",
              fontSize: 14,
              fontWeight: 600,
              minWidth: "200px",
              backgroundColor: "#9c27b0",
              borderRadius: "8px",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              boxShadow: "0 4px 12px rgba(156, 39, 176, 0.3)",
              "&:hover": {
                backgroundColor: "#7b1fa2",
                boxShadow: "0 6px 16px rgba(156, 39, 176, 0.4)",
                transform: "translateY(-2px)",
              },
              "&:disabled": {
                backgroundColor: "#e0e0e0",
                color: "#9e9e9e",
              },
              transition: "all 0.3s ease-in-out",
            }}
          >
            {isDownloadingImage ? (
              <>
                <Box
                  sx={{
                    display: "inline-block",
                    width: "16px",
                    height: "16px",
                    border: "2px solid #fff",
                    borderTop: "2px solid transparent",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    marginRight: "8px",
                  }}
                />
                Generating Image...
              </>
            ) : (
              "Download as Image"
            )}
          </Button>

          {/* PDF Download Button */}
          <Button
            variant="contained"
            startIcon={<Download />}
            disabled={isDownloadingPdf}
            onClick={downloadAsPdf}
            sx={{
              padding: "12px 24px",
              fontSize: 14,
              fontWeight: 600,
              minWidth: "200px",
              backgroundColor: "#2196f3",
              borderRadius: "8px",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              boxShadow: "0 4px 12px rgba(33, 150, 243, 0.3)",
              "&:hover": {
                backgroundColor: "#1976d2",
                boxShadow: "0 6px 16px rgba(33, 150, 243, 0.4)",
                transform: "translateY(-2px)",
              },
              "&:disabled": {
                backgroundColor: "#e0e0e0",
                color: "#9e9e9e",
              },
              transition: "all 0.3s ease-in-out",
            }}
          >
            {isDownloadingPdf ? (
              <>
                <Box
                  sx={{
                    display: "inline-block",
                    width: "16px",
                    height: "16px",
                    border: "2px solid #fff",
                    borderTop: "2px solid transparent",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    marginRight: "8px",
                  }}
                />
                Generating PDF...
              </>
            ) : (
              "Download as PDF"
            )}
          </Button>
        </Box>

        {/* Loading progress indicators */}
        {(isDownloadingImage || isDownloadingPdf) && (
          <Box sx={{ marginTop: "12px" }}>
            <Typography variant="body2" color="text.secondary">
              {isDownloadingImage
                ? "Please wait while the image is being generated..."
                : "Please wait while the PDF is being generated..."}
            </Typography>
          </Box>
        )}
      </Box>
    </div>
  );
};

export default SchoolRegisterTemplate9thClass;
